import { useState, useEffect, useCallback } from 'react'
import { FiCheck, FiX, FiTrash2, FiPlus, FiInbox, FiUser } from 'react-icons/fi'
import toast from 'react-hot-toast'
import api, { empleadosAPI } from '../../services/api'
import { Btn, Pill } from '../../ui'
import { formatearFecha } from '../../utils/fechas'
import { resumirOperacion, estadoCorreccionPill } from '../../utils/correcciones'
import CorreccionDiaModal from './CorreccionDiaModal'

/*
 * CorreccionesDiaAdmin — Punto C (vista administrador).
 * Lista las solicitudes-bloque del modelo nuevo (correcciones_dia) y permite:
 *  - Aprobar / rechazar (todo-o-nada) las que solicita el empleado.
 *  - Proponer una corrección a un empleado (flujo inverso).
 *  - Ver el estado de las propuestas del admin pendientes de confirmación.
 *
 * Props:
 *  - onCambio?: () => void   (refrescar listados del padre tras aprobar)
 */
const CorreccionesDiaAdmin = ({ onCambio }) => {
  const [filtro, setFiltro] = useState('pendiente') // 'pendiente' | 'todas'
  const [items, setItems] = useState([])
  const [empleados, setEmpleados] = useState([])
  const [procesando, setProcesando] = useState(null)
  const [comentarios, setComentarios] = useState({})
  const [proponer, setProponer] = useState(null) // { empleadoId, empleadoNombre } | null
  const [selEmpleado, setSelEmpleado] = useState('')

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/correcciones/dia/todas', { params: { estado: filtro } })
      setItems(data || [])
    } catch (e) { toast.error(e.message || 'Error al cargar correcciones') }
  }, [filtro])

  useEffect(() => { cargar() }, [cargar])
  useEffect(() => { empleadosAPI.getAll().then(r => setEmpleados((r.data || []).filter(e => !e.is_admin || e.rol === 'empleado'))).catch(() => {}) }, [])

  const responder = async (id, accion) => {
    setProcesando(id)
    try {
      await api.post(`/correcciones/dia/${id}/responder`, { accion, comentario: comentarios[id] || '' })
      toast.success(accion === 'aprobar' ? 'Corrección aprobada y aplicada' : 'Corrección rechazada')
      cargar(); onCambio?.()
    } catch (e) {
      toast.error(e.message || 'No se pudo procesar')
    } finally { setProcesando(null) }
  }

  const cancelarPropuesta = async (id) => {
    if (!window.confirm('¿Cancelar esta propuesta?')) return
    setProcesando(id)
    try {
      await api.delete(`/correcciones/dia/${id}`)
      toast.success('Propuesta cancelada'); cargar()
    } catch (e) { toast.error(e.message || 'No se pudo cancelar') }
    finally { setProcesando(null) }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="inline-flex rounded-pill bg-[var(--bg-2)] border border-[var(--line)] p-0.5">
          {[['pendiente', 'Pendientes'], ['todas', 'Histórico']].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setFiltro(v)}
              className={`px-3 py-1 rounded-pill text-[12.5px] ${filtro === v ? 'bg-[var(--ink)] text-white' : 'text-[var(--ink-2)]'}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <select value={selEmpleado} onChange={(e) => setSelEmpleado(e.target.value)}
            className="px-2 py-1.5 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[12.5px] focus:outline-none">
            <option value="">Empleado…</option>
            {empleados.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
          </select>
          <Btn type="button" variant="primary" className="!py-1.5 !px-3 text-[13px] flex items-center gap-1.5"
            disabled={!selEmpleado}
            onClick={() => {
              const emp = empleados.find(x => String(x.id) === String(selEmpleado))
              setProponer({ empleadoId: Number(selEmpleado), empleadoNombre: emp?.nombre })
            }}>
            <FiPlus size={13} /> Proponer
          </Btn>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="flex items-center gap-2 text-[13px] text-[var(--ink-3)] py-6 justify-center"><FiInbox size={15} /> No hay correcciones {filtro === 'pendiente' ? 'pendientes' : 'en el histórico'}.</div>
      ) : (
        <div className="space-y-3">
          {items.map((c) => {
            const pill = estadoCorreccionPill(c.estado)
            const esPropuestaAdmin = c.tipo_solicitud === 'admin_a_empleado'
            const puedeResponder = c.estado === 'pendiente' && !esPropuestaAdmin
            return (
              <div key={c.id} className="rounded-soft border border-[var(--line)] bg-[var(--bg-1)] p-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 text-[13px]">
                    <FiUser size={13} className="text-[var(--ink-3)]" />
                    <span className="font-medium text-[var(--ink)]">{c.empleado_nombre}</span>
                    <span className="text-[var(--ink-3)]">· {formatearFecha(c.fecha)}</span>
                    {esPropuestaAdmin && <span className="text-[11px] text-[rgba(120,80,200,1)]">propuesta del admin</span>}
                  </div>
                  <Pill tone={pill.tone}>{pill.label}</Pill>
                </div>
                {c.motivo && <p className="text-[12px] text-[var(--ink-2)] mt-1">{c.motivo}</p>}
                <ul className="space-y-0.5 text-[12px] text-[var(--ink-2)] mt-1">
                  {(c.operaciones || []).map((op, i) => <li key={i}>· {resumirOperacion(op)}</li>)}
                </ul>

                {puedeResponder && (
                  <div className="mt-2 space-y-2">
                    <input value={comentarios[c.id] || ''} onChange={(e) => setComentarios((m) => ({ ...m, [c.id]: e.target.value }))}
                      placeholder="Comentario (opcional)"
                      className="w-full px-2 py-1 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[12px] focus:outline-none" />
                    <div className="flex gap-2">
                      <Btn type="button" variant="primary" disabled={procesando === c.id} onClick={() => responder(c.id, 'aprobar')} className="!py-1 !px-2.5 text-[12px] flex items-center gap-1"><FiCheck size={12} /> Aprobar</Btn>
                      <Btn type="button" variant="ghost" disabled={procesando === c.id} onClick={() => responder(c.id, 'rechazar')} className="!py-1 !px-2.5 text-[12px] flex items-center gap-1"><FiX size={12} /> Rechazar</Btn>
                    </div>
                  </div>
                )}
                {esPropuestaAdmin && c.estado === 'pendiente' && (
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[11px] text-[var(--ink-3)]">Pendiente de confirmación del empleado</span>
                    <button type="button" disabled={procesando === c.id} onClick={() => cancelarPropuesta(c.id)} className="text-red-400 hover:text-red-600 flex items-center gap-1 text-[11px]"><FiTrash2 size={11} /> Cancelar propuesta</button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {proponer && (
        <CorreccionDiaModal
          modo="admin"
          empleadoId={proponer.empleadoId}
          empleadoNombre={proponer.empleadoNombre}
          onClose={() => setProponer(null)}
          onSuccess={() => { setProponer(null); cargar() }}
        />
      )}
    </div>
  )
}

export default CorreccionesDiaAdmin
